import React from 'react';
import { Grid, Tooltip, Typography, IconButton, Button, Box } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import ArchiveIcon from '@mui/icons-material/Archive';

const rowTextStyle = {
    fontFamily: 'Inter',
    fontWeight: 600,
    fontSize: '13px!important',
    lineHeight: '22px',
    letterSpacing: '-0.04em',
    color: '#000453',
};

const PatientRow = ({
    patient,
    selectedRef,
    isSelected,
    onEdit,
    onArchive,
    onView,
    hasWhiteboard,
    isMobileOrTablet,
    hideWhiteboardColumn = false,
    whiteboardCount = 0,
    handoutBreakdown = { whiteboards: 0, customised: 0 },
    customColumnWidths,
    compactStatus,
    isFirstItem = false,
    isLastItem = false,
}) => {
    const getColumnWidth = (key) => {
        if (customColumnWidths?.[key]) return customColumnWidths[key];
        if (isMobileOrTablet) {
            return key === 'patientName' || key === 'clientSurname' ? 3 : 2;
        }
        return key === 'microchipNumber' || key === 'breed' ? 2 : 1;
    };

    const totalHandouts = (handoutBreakdown?.whiteboards || 0) + (handoutBreakdown?.customised || 0);
    const whiteboardPortion =
        totalHandouts > 0 ? Math.round(((handoutBreakdown?.whiteboards || 0) / totalHandouts) * 100) : 0;
    const chartBackground =
        totalHandouts > 0
            ? `conic-gradient(#6A81FF 0% ${whiteboardPortion}%, #FF669B ${whiteboardPortion}% 100%)`
            : '#E0E0E0';
    const breakdownTooltip = (
        <Box sx={{ whiteSpace: 'pre-line', fontSize: 12 }}>
            {`Whiteboards: ${handoutBreakdown?.whiteboards || 0}\nCustomised: ${handoutBreakdown?.customised || 0}`}
        </Box>
    );

    return isMobileOrTablet ? (
        <Box
            ref={isSelected ? selectedRef : null}
            sx={{
                backgroundColor: isSelected ? '#E8EAF6' : '#FFFFFF',
                p: 1,
                borderBottom: isLastItem ? 'none' : '1px solid #c6c6c6',
                borderTopLeftRadius: isFirstItem ? '8px' : 0,
                borderTopRightRadius: isFirstItem ? '8px' : 0,
                borderBottomLeftRadius: isLastItem ? '8px' : 0,
                borderBottomRightRadius: isLastItem ? '8px' : 0,
                color: '#000453',
            }}
        >
            <Grid container sx={{ padding: 1 }}>
                <Grid item xs={12} display="flex" flexDirection="column">
                    <Typography fontWeight="bold" mb={0.5}>
                        {patient.patientName}
                    </Typography>
                    <Typography fontWeight="bold" mb={1}>
                        {patient.clientSurname}
                    </Typography>

                    <Typography mb={0.5}>{patient.species}</Typography>
                    <Typography mb={0.5}>{patient.breed}</Typography>
                    <Typography mb={0.5}>{patient.DOB}</Typography>
                    <Typography mb={0.5}>{patient.microchipNumber}</Typography>
                    <Typography mb={1}>{patient.clinicPatientId}</Typography>

                    <Box display={'flex'} justifyContent={'space-between'}>
                        <Box
                            sx={{
                                width: '72px',
                                height: '30px',
                                borderRadius: '16px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontFamily: 'Inter',
                                fontWeight: 500,
                                fontSize: '12.78px',
                                lineHeight: '16px',
                                textAlign: 'center',
                                backgroundColor:
                                    patient.patientStatus === 'active'
                                        ? 'rgba(0, 137, 0, 0.15)'
                                        : 'rgba(220, 0, 37, 0.15)',
                                color: patient.patientStatus === 'active' ? '#008900' : '#DC0025',
                                mb: 2,
                            }}
                        >
                            {patient.patientStatus === 'active' ? 'Active' : 'Archived'}
                        </Box>

                        {!hideWhiteboardColumn && (
                            <Tooltip title={breakdownTooltip} arrow disableHoverListener={!hasWhiteboard}>
                                <span>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        disabled={!hasWhiteboard}
                                        onClick={() => onView(patient)}
                                        sx={{
                                            width: '200px',
                                            height: '34px',
                                            fontWeight: 500,
                                            fontFamily: 'Inter',
                                            fontSize: '13px',
                                            lineHeight: '16px',
                                            textTransform: 'none',
                                            color: '#FF669B',
                                            borderColor: '#FF669B',
                                            borderRadius: '8px',
                                            backgroundColor: '#FFFFFF',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: 1,
                                            '&:hover': {
                                                borderColor: '#FF669B',
                                            },
                                            '&.Mui-disabled': {
                                                color: '#C4C4C4',
                                                borderColor: '#C4C4C4',
                                                backgroundColor: '#F1F1F1',
                                            },
                                            mb: 1,
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: 16,
                                                height: 16,
                                                borderRadius: '50%',
                                                background: chartBackground,
                                                border: '1px solid #FF669B33',
                                            }}
                                        />
                                        View Handouts ({whiteboardCount || 0})
                                    </Button>
                                </span>
                            </Tooltip>
                        )}
                    </Box>

                    <Box display="flex" gap={1} position={'absolute'} right={24}>
                        <Tooltip title="Edit patient" arrow>
                            <IconButton onClick={() => onEdit(patient)} sx={{ color: '#000453' }}>
                                <EditIcon />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Archive patient" arrow>
                            <IconButton onClick={() => onArchive(patient.patientId)} sx={{ color: '#000453' }}>
                                <ArchiveIcon />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Grid>
            </Grid>
        </Box>
    ) : (
        <Box sx={{ minWidth: '1100px' }}>
            <Grid
                container
                alignItems="center"
                ref={isSelected ? selectedRef : null}
                sx={{
                    backgroundColor: isSelected ? '#E8EAF6' : 'transparent',
                    height: '60px',
                    px: 2,
                    borderTop: '1.4px solid #0000001A',
                    '&:hover': { backgroundColor: '#EFEFEF' },
                }}
            >
                {/* Name */}
                <Grid item xs={getColumnWidth('patientName')}>
                    <Typography
                        sx={{
                            ...rowTextStyle,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        }}
                    >
                        {patient.patientName}
                    </Typography>
                </Grid>

                {/* Surname */}
                {!isMobileOrTablet && (
                    <Grid item xs={getColumnWidth('clientSurname')}>
                        <Typography
                            sx={{
                                ...rowTextStyle,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                            }}
                        >
                            {patient.clientSurname}
                        </Typography>
                    </Grid>
                )}

                {/* Only show these columns on desktop */}
                {!isMobileOrTablet && (
                    <>
                        <Grid item xs={getColumnWidth('DOB')}>
                            <Typography sx={rowTextStyle}>{patient.DOB}</Typography>
                        </Grid>
                        <Grid item xs={getColumnWidth('microchipNumber')}>
                            <Typography sx={rowTextStyle}>{patient.microchipNumber}</Typography>
                        </Grid>
                        <Grid item xs={getColumnWidth('species')}>
                            <Typography sx={rowTextStyle}>{patient.species}</Typography>
                        </Grid>
                        <Grid item xs={getColumnWidth('breed')}>
                            <Typography sx={rowTextStyle}>{patient.breed}</Typography>
                        </Grid>
                        <Grid item xs={getColumnWidth('clinicPatientId')}>
                            <Typography sx={rowTextStyle}>{patient.clinicPatientId}</Typography>
                        </Grid>
                    </>
                )}

                {/* Status */}
                <Grid item xs={getColumnWidth('patientStatus')}>
                    <Box
                        sx={{
                            width: compactStatus
                                ? isMobileOrTablet
                                    ? '30px'
                                    : '72px'
                                : isMobileOrTablet
                                  ? '30px'
                                  : '96px',
                            height: '30px',
                            borderRadius: '16px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontFamily: 'Inter',
                            fontWeight: 500,
                            fontSize: '12px',
                            lineHeight: '16px',
                            textAlign: 'center',
                            backgroundColor:
                                patient.patientStatus === 'active' ? 'rgba(0, 137, 0, 0.15)' : 'rgba(220, 0, 37, 0.15)',
                            color: patient.patientStatus === 'active' ? '#008900' : '#DC0025',
                        }}
                    >
                        {patient.patientStatus === 'active' && !isMobileOrTablet
                            ? 'Active'
                            : !isMobileOrTablet
                              ? 'Archived'
                              : null}
                    </Box>
                </Grid>

                {/* Operation */}
                <Grid item xs={getColumnWidth('actions')}>
                    <Tooltip title="Edit patient" arrow>
                        <IconButton onClick={() => onEdit(patient)} sx={{ color: '#000453' }}>
                            <EditIcon />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Archive patient" arrow>
                        <IconButton onClick={() => onArchive(patient.patientId)} sx={{ color: '#000453' }}>
                            <ArchiveIcon />
                        </IconButton>
                    </Tooltip>
                </Grid>

                {/* Whiteboard View */}
                {!hideWhiteboardColumn && (
                    <Grid item xs={getColumnWidth('whiteboards')}>
                        <Tooltip title={breakdownTooltip} arrow disableHoverListener={!hasWhiteboard}>
                            <span>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    disabled={!hasWhiteboard}
                                    onClick={() => onView(patient)}
                                    sx={{
                                        width: '130px',
                                        height: '32px',
                                        fontWeight: 500,
                                        fontFamily: 'Inter',
                                        fontSize: '12px',
                                        lineHeight: '16px',
                                        textTransform: 'none',
                                        color: '#FF669B',
                                        borderColor: '#FF669B',
                                        borderRadius: '8px',
                                        backgroundColor: '#FFFFFF',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: 1,
                                        '&:hover': {
                                            backgroundColor: '#FFFFFF',
                                            borderColor: '#FF669B',
                                        },
                                        '&.Mui-disabled': {
                                            color: '#C4C4C4',
                                            borderColor: '#C4C4C4',
                                            backgroundColor: '#F1F1F1',
                                        },
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 14,
                                            height: 14,
                                            borderRadius: '50%',
                                            background: chartBackground,
                                            border: '1px solid #FF669B33',
                                        }}
                                    />
                                    View ({whiteboardCount || 0})
                                </Button>
                            </span>
                        </Tooltip>
                    </Grid>
                )}
            </Grid>
        </Box>
    );
};

export default PatientRow;
